import React, { useState, useCallback, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, MapPin, Search, ChevronRight } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGlobal } from "@/context/GlobalContext";
import { weekOf, fmtDT } from "@/lib/semanas";
import { WeekPicker } from "@/components/control/WeekPicker";
import { CompraEditor } from "@/components/control/CompraEditor";
import { ExportExcelButton } from "@/components/ExportExcelButton";
import { COMPRA } from "@/lib/configSemanal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PemexPrecios } from "@/components/control/PemexPrecios";
import { PemexComparar } from "@/components/control/PemexComparar";

function Estado({ r }) {
  if (r.estado === "guardada") return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700" data-testid={`compra-estado-${r.planta}`}>Guardada</span>;
  if (r.estado === "heredada") return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700" data-testid={`compra-estado-${r.planta}`}>Heredada de S{r.origen_week} {r.origen_year}</span>;
  return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-500" data-testid={`compra-estado-${r.planta}`}>Sin configuración</span>;
}

export function useUnsavedGuard() {
  const dirtyRef = useRef(new Set());
  const [pending, setPending] = useState(null);
  const discardedRef = useRef(false);
  const onDirtyChange = useCallback((planta, d) => { if (d) dirtyRef.current.add(planta); else dirtyRef.current.delete(planta); }, []);
  const guard = useCallback((fn, cancel) => { if (dirtyRef.current.size) setPending({ fn, cancel }); else fn(); }, []);
  useEffect(() => {
    const h = (e) => { if (dirtyRef.current.size) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);
  const dialog = (
    <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) { if (!discardedRef.current) pending?.cancel?.(); discardedRef.current = false; setPending(null); } }}>
      <AlertDialogContent className="bg-white text-slate-900" data-testid="compra-unsaved-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Cambios sin guardar</AlertDialogTitle>
          <AlertDialogDescription>Hay cambios en la configuración que no se han guardado. Si continúas, se perderán.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="compra-unsaved-cancel">Seguir editando</AlertDialogCancel>
          <AlertDialogAction data-testid="compra-unsaved-discard" className="bg-red-600 hover:bg-red-700" onClick={() => { discardedRef.current = true; dirtyRef.current.clear(); pending?.fn?.(); }}>Descartar cambios</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return { onDirtyChange, guard, dialog };
}

export default function PreciosCompra({ mode = COMPRA }) {
  const { user } = useAuth();
  const { structureKey, node, currentStructure } = useGlobal();
  const canEdit = user?.is_admin || (user?.caps || []).includes(mode.cap);
  const [yw, setYWRaw] = useState(weekOf());
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const { onDirtyChange, guard, dialog } = useUnsavedGuard();
  const setYW = (v) => guard(() => setYWRaw(v));
  const toggle = (p) => guard(() => setOpen((o) => (o === p ? null : p)));
  const { data, isLoading } = useQuery({
    queryKey: [mode.qk, "plantas", structureKey, node?.id ?? "entire", yw.year, yw.week],
    queryFn: async () => (await api.get(`/control-semanal/${mode.base}/plantas`, { params: { structure: structureKey, node_id: node?.id, ...yw } })).data.items,
    enabled: !!structureKey,
  });
  const rows = (data || []).filter((r) => !q || r.planta.toLowerCase().includes(q.toLowerCase()));
  const config = (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <WeekPicker year={yw.year} week={yw.week} setYW={setYW} />
        <div className="relative ml-auto">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar planta" data-testid="compra-search"
            className="h-9 w-64 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 focus:outline-none" />
        </div>
        {!!rows.length && <ExportExcelButton testid={`${mode.base}-export`} fileName={`${mode.title}_${yw.year}_S${String(yw.week).padStart(2, "0")}`}
          titulo={`Semana ${yw.week} de ${yw.year}${node ? ` · ${node.name}` : ""}`}
          hojas={[{ name: "Plantas", selector: '[data-testid="compra-plantas-table"]' },
            ...rows.map((r) => ({ name: r.planta, node: <CompraEditor mode={mode} planta={r.planta} year={yw.year} week={yw.week} structure={structureKey} canEdit={false} onDirtyChange={() => {}} /> }))]} />}
      </div>
      {isLoading ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div> : (
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="compra-plantas-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-4 py-2.5">Planta</th><th className="text-left px-4 py-2.5">Configuración de la semana</th><th className="text-right px-4 py-2.5">{mode.countLabel}</th><th className="text-left px-4 py-2.5">Última actualización</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.planta}>
                  <tr onClick={() => toggle(r.planta)} className={`border-t border-slate-100 cursor-pointer hover:bg-red-50/20 ${open === r.planta ? "bg-red-50/30" : ""}`} data-testid={`compra-planta-${r.planta}`}>
                    <td className="px-4 py-2.5 font-semibold text-slate-800 whitespace-nowrap"><ChevronRight data-testid={`compra-expand-${r.planta}`} className={`w-4 h-4 inline text-slate-400 mr-1 transition-transform duration-200 ${open === r.planta ? "rotate-90" : ""}`} /><MapPin className="w-3.5 h-3.5 inline text-red-500 mr-1" />{r.planta}</td>
                    <td className="px-4 py-2.5"><Estado r={r} /></td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{r.estado === "sin_configuracion" ? "—" : `${r[mode.countKey]} / ${r.total_conceptos}`}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{r.updated_at ? `${r.updated_by_name} · ${fmtDT(r.updated_at)}${r.estado === "heredada" ? ` (de S${r.origen_week} ${r.origen_year})` : ""}` : "—"}</td>
                  </tr>
                  {open === r.planta && (
                    <tr className="bg-slate-50/60 animate-in fade-in slide-in-from-top-1 duration-200">
                      <td colSpan={4}><CompraEditor mode={mode} planta={r.planta} year={yw.year} week={yw.week} structure={structureKey} canEdit={canEdit} onDirtyChange={onDirtyChange} /></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {!rows.length && <tr><td colSpan={4} className="py-10 text-center text-slate-400" data-testid="compra-empty">Sin plantas</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
  return (
    <div className="w-full space-y-6" data-testid={mode.base === "compra" ? "precios-compra" : "precios-flete"}>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Control Semanal</p>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">{mode.title}</h1>
        <p className="text-sm text-slate-500 mt-1">{mode.desc} {currentStructure?.name ? `Estructura: ${currentStructure.name}${node ? ` · ${node.name}` : " · Toda la estructura"}` : ""}</p>
      </div>
      {mode.base === "compra" ? (
        <Tabs defaultValue="pemex">
          <TabsList data-testid="compra-tabs">
            <TabsTrigger value="pemex" data-testid="compra-tab-pemex">Precios PEMEX</TabsTrigger>
            <TabsTrigger value="comparar" data-testid="compra-tab-comparar">Comparación de precios</TabsTrigger>
            <TabsTrigger value="config" data-testid="compra-tab-config">Configuración de precios</TabsTrigger>
          </TabsList>
          <TabsContent value="config" className="mt-4 data-[state=inactive]:hidden" forceMount>{config}</TabsContent>
          <TabsContent value="pemex" className="mt-4"><PemexPrecios /></TabsContent>
          <TabsContent value="comparar" className="mt-4"><PemexComparar /></TabsContent>
        </Tabs>
      ) : config}
      {dialog}
    </div>
  );
}
